import { z } from 'zod';

import { billingEntityTypeSchema, type BillingEntityType } from './enums.js';

/**
 * FATURA PROFİLİ — Genişleme Adım 9.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu dosyanın çözdüğü ENGEL
 * ═══════════════════════════════════════════════════════════════════════════
 * İyzico'ya bugüne kadar `PLACEHOLDER_BUYER` gidiyordu: sabit bir TCKN
 * (`11111111111`), sabit bir adres ve `splitName(workspaceName, email)` ile
 * UYDURULMUŞ bir ad/soyad. Sandbox bunu kabul ediyor, **production
 * reddediyor** — Türkiye'de e-arşiv faturası için `identityNumber`,
 * `gsmNumber` ve fatura adresi yasal zorunluluk.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 DOĞRULAMA KAYDETMEDEN ÖNCE — Adım 2'nin kuralı
 * ═══════════════════════════════════════════════════════════════════════════
 * Geçersiz bir TCKN/VKN'yi kaydedip ilk ödemede öğrenmek, kullanıcıya
 * "fatura bilgileriniz kaydedildi" deyip parasını çekemeden reddettirmektir.
 * Checksum burada, sözleşmenin içinde — yani arayüz ile sunucu aynı kararı
 * veriyor ve arayüz "kaydet"e basılmadan söyleyebiliyor.
 *
 * `11111111111` (iyzico'nun kendi sandbox örneği) bu checksum'dan GEÇMİYOR;
 * yani yer tutucu değer gerçek bir profil olarak geri giremiyor.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Kimlik numarası doğrulaması
// ═══════════════════════════════════════════════════════════════════════════

/**
 * TCKN (T.C. Kimlik Numarası) — 11 hane, iki kontrol basamağı.
 *
 * d10 = ((d1+d3+d5+d7+d9) × 7 − (d2+d4+d6+d8)) mod 10
 * d11 = (d1..d10 toplamı) mod 10
 *
 * İlk hane 0 olamaz. Algoritma resmî ve sabit; "belki bir gün değişir" diye
 * gevşetmek, tam olarak yakalaması gereken hatayı kaçırmak olurdu.
 */
export function isValidTckn(value: string): boolean {
  if (!/^[1-9][0-9]{10}$/.test(value)) return false;

  const d = [...value].map(Number) as number[];
  const odd = d[0]! + d[2]! + d[4]! + d[6]! + d[8]!;
  const even = d[1]! + d[3]! + d[5]! + d[7]!;

  // Çıkarma negatif olabilir; `% 10` JavaScript'te işareti koruyor, o yüzden
  // +10 ile normalize ediliyor. (`-3 % 10 === -3` — kontrol basamağı asla
  // negatif olamaz, bu satır olmadan geçerli numaralar reddedilirdi.)
  const check10 = (((odd * 7 - even) % 10) + 10) % 10;
  if (check10 !== d[9]) return false;

  const check11 = d.slice(0, 10).reduce((sum, n) => sum + n, 0) % 10;
  return check11 === d[10];
}

/**
 * VKN (Vergi Kimlik Numarası) — 10 hane, tek kontrol basamağı.
 *
 * Her hane sırasına göre kaydırılıp ikinin kuvvetiyle çarpılıyor ve 9'a göre
 * modu alınıyor; 9 çıkan basamak olduğu gibi toplanıyor (mod 9'da 9 ≡ 0
 * olduğu için aksi hâlde bilgi kaybolurdu).
 */
export function isValidVkn(value: string): boolean {
  if (!/^[0-9]{10}$/.test(value)) return false;

  const d = [...value].map(Number) as number[];
  let sum = 0;

  for (let i = 0; i < 9; i += 1) {
    const shifted = (d[i]! + 9 - i) % 10;
    sum += shifted === 9 ? shifted : (shifted * 2 ** (9 - i)) % 9;
  }

  return (10 - (sum % 10)) % 10 === d[9];
}

/// Türe göre doğru algoritmayı seçer. Uzunluğa bakıp TAHMİN ETMİYOR:
/// `entityType` kullanıcının beyanı ve fatura başlığı da ona göre kesiliyor.
export function isValidIdentityNumber(entityType: BillingEntityType, value: string): boolean {
  return entityType === 'INDIVIDUAL' ? isValidTckn(value) : isValidVkn(value);
}

/**
 * Yanıtta gösterilen hâl: son 4 hane dışında maskeli (`•••••••8901`).
 *
 * 🔴 Kolon DÜZ METİN saklanıyor (kullanıcı kararı, gerekçe şemada) — koruma
 * tam olarak burada. Tam değer yalnız ödeme sağlayıcısına ve e-belge
 * entegratörüne gidiyor; hiçbir `GET` yanıtı, hiçbir log satırı görmüyor.
 *
 * Maske UZUNLUĞU gerçek uzunluğu ele veriyor ve bu bilinçli: kullanıcı
 * "kurumsal numara mı girmişim" sorusunu ekrandan cevaplayabilmeli.
 */
export function maskIdentityNumber(value: string): string {
  if (value.length <= 4) return '•'.repeat(value.length);
  return '•'.repeat(value.length - 4) + value.slice(-4);
}

// ═══════════════════════════════════════════════════════════════════════════
// Telefon
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Türk cep telefonunu E.164'e (`+905351234567`) çevirir; çeviremezse `null`.
 *
 * Kabul edilen girdiler: `0535 123 45 67` · `535 123 45 67` · `905351234567`
 * · `+90 535 123 45 67`. Boşluk, tire ve parantez atılıyor.
 *
 * 🔴 Neden normalize ediliyor da "olduğu gibi" saklanmıyor: iyzico
 * `gsmNumber`ı E.164 bekliyor ve biçimi bozuk numarayı SESSİZCE kabul edip
 * faturaya basıyor. Kullanıcının yazdığı boşluklu hâli saklayıp sağlayıcıya
 * göndermek, hatayı ilk gerçek faturada görmek demekti.
 *
 * Yurt dışı numarası (`+49…`) olduğu gibi kabul ediliyor: sadece Türkiye'ye
 * kısıtlamak, yurt dışından ödeyen bir müşteriyi kapıda çevirirdi.
 */
export function normalizePhone(raw: string): string | null {
  const cleaned = raw.replace(/[\s()\-.]/g, '');

  if (/^\+90[0-9]{10}$/.test(cleaned)) return cleaned;
  if (/^90[0-9]{10}$/.test(cleaned)) return `+${cleaned}`;
  if (/^0[0-9]{10}$/.test(cleaned)) return `+90${cleaned.slice(1)}`;
  if (/^[1-9][0-9]{9}$/.test(cleaned)) return `+90${cleaned}`;
  // Diğer ülkeler: `+` ile başlayan 8–15 haneli her şey.
  if (/^\+[1-9][0-9]{7,14}$/.test(cleaned)) return cleaned;

  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Giriş şeması
// ═══════════════════════════════════════════════════════════════════════════

const trimmed = (max: number) => z.string().trim().min(1).max(max);

/**
 * `PUT /billing/profile` gövdesi.
 *
 * Kurumsal alanların zorunluluğu `superRefine` ile: `entityType` COMPANY ise
 * `companyTitle` ve `taxOffice` DOLU olmak zorunda. Zod'un `discriminatedUnion`
 * ile de yazılabilirdi ve yazılmadı — hata mesajı alan bazında dönmeli
 * (`fieldErrors`), birleşimde ise "hiçbir varyanta uymadı" gibi arayüzde
 * gösterilemeyen bir hata çıkıyor.
 */
export const billingProfileInputSchema = z
  .object({
    entityType: billingEntityTypeSchema,

    firstName: trimmed(80),
    lastName: trimmed(80),

    companyTitle: trimmed(160).optional(),
    taxOffice: trimmed(120).optional(),

    /// Boşluk/tire temizleniyor: kullanıcı `123 456 789 01` yazabilir.
    identityNumber: z
      .string()
      .trim()
      .transform((v) => v.replace(/[\s-]/g, '')),

    phone: z.string().trim().min(1).max(32),

    addressLine: trimmed(300),
    district: trimmed(80).optional(),
    city: trimmed(80),
    postalCode: trimmed(20).optional(),
    countryCode: z
      .string()
      .trim()
      .length(2)
      .transform((v) => v.toUpperCase())
      .default('TR'),

    billingEmail: z.string().trim().email().max(160).optional(),
  })
  .superRefine((value, ctx) => {
    if (!isValidIdentityNumber(value.entityType, value.identityNumber)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['identityNumber'],
        message:
          value.entityType === 'INDIVIDUAL'
            ? 'Geçersiz TCKN — 11 haneli ve doğrulama basamakları tutmalı.'
            : 'Geçersiz VKN — 10 haneli ve doğrulama basamağı tutmalı.',
      });
    }

    if (normalizePhone(value.phone) === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phone'],
        message: 'Geçersiz telefon numarası. Örnek: 0535 123 45 67',
      });
    }

    if (value.entityType === 'COMPANY') {
      if (!value.companyTitle) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['companyTitle'],
          message: 'Kurumsal faturada ticari unvan zorunlu.',
        });
      }
      if (!value.taxOffice) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['taxOffice'],
          message: 'Kurumsal faturada vergi dairesi zorunlu.',
        });
      }
    }
  });

export type BillingProfileInput = z.infer<typeof billingProfileInputSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yanıt
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `GET /billing/profile`.
 *
 * 🔴 `identityNumber` YOK — yerine `identityNumberMasked` var. Alan adını
 * aynı bırakıp maskeli değer koymak, bir gün birinin onu sağlayıcıya
 * göndermesine ve maskeli numarayla ödeme reddi almasına yol açardı.
 */
export interface BillingProfileResponse {
  entityType: BillingEntityType;
  firstName: string;
  lastName: string;
  companyTitle: string | null;
  taxOffice: string | null;
  identityNumberMasked: string;
  phone: string;
  addressLine: string;
  district: string | null;
  city: string;
  postalCode: string | null;
  countryCode: string;
  billingEmail: string | null;
  updatedAt: string;
}

/**
 * Profil yoksa `null` + neden gerektiği. Arayüz ödeme düğmesini buna bakarak
 * "önce fatura bilgilerinizi girin"e çeviriyor — kullanıcı sağlayıcıya gidip
 * orada reddedilmesin.
 */
export interface BillingProfileStateResponse {
  profile: BillingProfileResponse | null;
  /// Ödeme yapılabilmesi için profil şart mı. Bugün her zaman `true`;
  /// alan olarak duruyor çünkü ileride kurumsal havale gibi profil
  /// istemeyen bir sağlayıcı gelebilir ve arayüzün bunu env'den tahmin
  /// etmesi gerekmesin.
  required: boolean;
}

/**
 * Sağlayıcıya giden ALICI KİMLİĞİ — `PaymentProvider` sözleşmesinin girdisi.
 *
 * Profilden türetiliyor ve TAM kimlik numarasını taşıyor. `BillingProfileResponse`
 * ile aynı şey DEĞİL ve olmamalı: biri ekrana, biri sağlayıcıya gidiyor.
 */
export interface BuyerIdentity {
  firstName: string;
  lastName: string;
  /// Fatura başlığı: kurumsalda unvan, bireyselde ad soyad.
  contactName: string;
  email: string;
  /// E.164.
  phone: string;
  identityNumber: string;
  addressLine: string;
  city: string;
  district: string | null;
  postalCode: string | null;
  /// ISO-2. Sağlayıcının istediği metne çeviri adapter'ın içinde.
  countryCode: string;
}

/**
 * ISO-2 → iyzico'nun beklediği ülke METNİ.
 *
 * İyzico `country` alanına ISO kodu değil ad istiyor ("Turkey"). Eşleme
 * burada değil ADAPTER'da yapılmalıydı — ama tek satırlık bir tablo iki
 * sağlayıcı arasında paylaşılacak kadar genel; e-belge entegratörü de aynı
 * dönüşümü isteyecek. Bilinmeyen kod olduğu gibi geçiyor: uydurmak yerine
 * sağlayıcının reddetmesi daha dürüst.
 */
export const COUNTRY_NAME_BY_ISO2: Readonly<Record<string, string>> = {
  TR: 'Turkey',
  DE: 'Germany',
  GB: 'United Kingdom',
  US: 'United States',
  NL: 'Netherlands',
  FR: 'France',
  AZ: 'Azerbaijan',
};

/* Fatura formu artık her ülkeyi seçtiriyor (24 Eki 2026); tabloda olmayan kod
   için İngilizce bölge adı `Intl`den. Tablo önde kalıyor: Intl TR'ye
   "Türkiye" diyor, iyzico "Turkey" bekliyor. */
const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

export function countryNameOf(iso2: string): string {
  const code = iso2.toUpperCase();
  const known = COUNTRY_NAME_BY_ISO2[code];
  if (known) return known;
  try {
    return REGION_NAMES.of(code) ?? code;
  } catch {
    return code;
  }
}
